import { TypedEmitter } from '@/core/event-emitter';
import { ProjectBusEvents } from '@/project/bus/index';
import { GroupChatMessageViewObject, GroupChatViewObject, HubConnectionViewObject, NotificationViewObject, UserViewObject, CaseFileViewObject, CaseViewObject, EmailFlowViewObject, CasePersonViewObject } from '@/api';
import { onBeforeUnmount } from 'vue';

interface BusEvents extends ProjectBusEvents {
    'PROGRESS_START': () => void;
    'PROGRESS_FINISH': () => void;
    'SIGNALR_RECONNECT': (connectionId?: string) => void;
    'SIGNALR_DISCONNECT': () => void;
    'LOGGED_IN': () => void;
    'LOGGED_OUT': () => void;

    'UserCreated': (user: UserViewObject) => void;
    'UserUpdated': (user: UserViewObject) => void;
    'UsersUpdated': (users: UserViewObject[]) => void;
    'UserRemoved': (userId: string) => void;
    'OnConnected': (connection: HubConnectionViewObject) => void;
    'OnDisconnected': (userId: string, connectionId: string) => void;
    'OnConnectionUpdate': (connection: HubConnectionViewObject) => void;
    
    'ChatMessageReceived': (message: GroupChatMessageViewObject) => void;
    'ChatMarkedAsRead': (chatId: string) => void;
    'GroupChatCreatedOrUpdated': (chat: GroupChatViewObject) => void;
    'GroupChatRemoved': (chatId: string) => void;

    'NotificationCreated': (notification: NotificationViewObject) => void;
    'NotificationUpdated': (notification: NotificationViewObject) => void;
    'NotificationsUpdated': (notifications: NotificationViewObject[]) => void;
    'NotificationDeleted': (notificationId: string) => void;
    'CaseFileCreated': (file: CaseFileViewObject) => void;
    'CaseFileDeleted': (caseFileId: string) => void;
    'CaseUpdated': (caseData: CaseViewObject) => void;
    'CasePersonUpdated': (caseData: CasePersonViewObject) => void;
    'EmailFlowCreated': (flow: EmailFlowViewObject) => void;
    'EmailFlowUpdated': (flow: EmailFlowViewObject) => void;
    'EmailFlowDeleted': (emailFlowId: string) => void;
}

const bus = new TypedEmitter<BusEvents>();

export function useBusEvent<U extends keyof BusEvents>(event: U, listener: BusEvents[U]) {
    bus.on(event, listener);

    onBeforeUnmount(() => {
        bus.off(event, listener);
    });
}

export function useBusEvents(events: { [K in keyof Partial<BusEvents>]: BusEvents[K] }) {
    Object.entries(events).forEach(([event, listener]) => {
        if (listener) {
            bus.on(event as keyof BusEvents, listener as BusEvents[keyof BusEvents]);
        }
    });

    onBeforeUnmount(() => {
        Object.entries(events).forEach(([event, listener]) => {
            if (listener) {
                bus.off(event as keyof BusEvents, listener as BusEvents[keyof BusEvents]);
            }
        });
    });
}

export default bus;
